import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Compass,
  Sparkles,
  Building2,
  Landmark,
  Users,
  ShieldCheck,
  Leaf,
  FileText,
  FolderSearch,
  ClipboardList,
  SearchCheck,
  Gavel,
  CheckCircle2,
  ArrowRight,
  Download,
  MapPinned,
  CreditCard,
  Tag,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const BADGE_URLS = {
  gold: "https://customer-assets.emergentagent.com/job_travel-quality-label/artifacts/3qf4e4f0_Black%20And%20White%20Simple%20Icon%20Logo.png",
  silver: "https://customer-assets.emergentagent.com/job_travel-quality-label/artifacts/80ndwcjh_Black%20And%20White%20Simple%20Icon%20Logo%20%281%29.png",
  bronze: "https://customer-assets.emergentagent.com/job_travel-quality-label/artifacts/o5vrg8qt_Black%20And%20White%20Simple%20Icon%20Logo%20%282%29.png",
};

const destinationTypes = [
  "Cities", "Municipalities", "Towns", "Islands", "Regions",
  "Provinces", "States", "National Tourism Destinations",
  "UNESCO Heritage Destinations", "Tourism Development Areas",
];

const pillars = [
  {
    icon: Compass,
    title: "Destination Management & Governance",
    description:
      "The effectiveness of destination leadership, planning, stakeholder collaboration, and tourism management.",
    areas: [
      "Tourism strategy", "Destination management organization (DMO)",
      "Tourism planning", "Stakeholder collaboration", "Visitor management",
      "Performance measurement", "Tourism investment", "Destination branding",
    ],
  },
  {
    icon: Sparkles,
    title: "Visitor Experience",
    description:
      "The quality of experiences available to visitors before, during, and after their visit.",
    areas: [
      "Accessibility", "Visitor information", "Signage and wayfinding",
      "Customer service", "Attractions and experiences",
      "Digital visitor services", "Visitor satisfaction", "Destination reputation",
    ],
  },
  {
    icon: Building2,
    title: "Tourism Infrastructure & Quality",
    description:
      "The quality and readiness of tourism infrastructure supporting visitors.",
    areas: [
      "Accommodation supply", "Food and beverage offerings",
      "Transportation systems", "Public facilities",
      "Accessibility for all visitors", "Cleanliness",
      "Public realm quality", "Connectivity and technology",
    ],
  },
  {
    icon: Landmark,
    title: "Culture, Heritage & Identity",
    description:
      "The preservation, promotion, and celebration of local culture and heritage.",
    areas: [
      "Cultural authenticity", "Heritage conservation",
      "Museums and cultural assets", "Local traditions", "Architecture",
      "Storytelling and interpretation", "Festivals and events",
      "Destination identity",
    ],
  },
  {
    icon: Users,
    title: "Community & Economic Impact",
    description:
      "The contribution of tourism to local communities and economic development.",
    areas: [
      "Local employment", "Local business support", "Community participation",
      "Tourism benefits distribution", "Local sourcing",
      "Resident satisfaction", "Tourism inclusiveness", "Economic contribution",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Safety, Health & Security",
    description:
      "The destination's ability to provide a safe and secure environment for visitors and residents.",
    areas: [
      "Public safety", "Crime prevention", "Emergency preparedness",
      "Healthcare access", "Visitor safety information",
      "Crisis management", "Disaster preparedness", "Risk management",
    ],
  },
  {
    icon: Leaf,
    title: "Sustainability & Responsible Tourism",
    description:
      "The destination's commitment to environmental stewardship and responsible tourism development.",
    areas: [
      "Environmental protection", "Resource management", "Water conservation",
      "Energy efficiency", "Waste management", "Climate resilience",
      "Biodiversity protection", "Sustainable tourism policies",
    ],
  },
];

const stages = [
  {
    icon: FileText,
    code: "Stage 1",
    title: "Application & Registration",
    summary:
      "The destination submits its certification application and core information. The application is reviewed to determine eligibility and certification scope.",
    items: [
      "Certification Application",
      "Destination Profile",
      "Tourism Statistics",
      "Governance Information",
      "Stakeholder Contact Information",
    ],
    payCta: {
      label: "Pay Application Fee Here",
      href: "https://buy.stripe.com/6oU9ASeBU3Sw1dxeSL63K06",
    },
  },
  {
    icon: FolderSearch,
    code: "Stage 2",
    title: "Documentation Review",
    summary:
      "Destinations submit evidence supporting compliance with WTTQ.Org standards across six evidence categories.",
    groups: [
      {
        heading: "Governance & Planning",
        items: [
          "Tourism Master Plan", "Destination Strategy",
          "Tourism Development Plans", "Visitor Management Plans",
          "Annual Tourism Reports",
        ],
      },
      {
        heading: "Visitor Experience",
        items: [
          "Visitor Guides", "Marketing Materials", "Destination Websites",
          "Visitor Satisfaction Studies", "Accessibility Information",
        ],
      },
      {
        heading: "Culture & Heritage",
        items: [
          "Heritage Conservation Plans", "Cultural Programs",
          "Festival Documentation", "Museum Information",
          "Cultural Asset Inventories",
        ],
      },
      {
        heading: "Community & Economic Development",
        items: [
          "Community Engagement Programs", "Economic Impact Studies",
          "Local Supplier Programs", "Workforce Development Initiatives",
        ],
      },
      {
        heading: "Safety & Security",
        items: [
          "Emergency Plans", "Risk Assessments", "Public Safety Reports",
          "Healthcare Access Information",
        ],
      },
      {
        heading: "Sustainability",
        items: [
          "Sustainability Strategy", "Environmental Policies",
          "Waste Management Plans", "Climate Action Plans",
          "Sustainability Reports",
        ],
      },
    ],
  },
  {
    icon: ClipboardList,
    code: "Stage 3",
    title: "Destination Excellence Questionnaire",
    summary:
      "Destinations complete the WTTQ.Org Destination Excellence Assessment. The assessment evaluates performance across all seven certification pillars. Each criterion is scored using the WTTQ.Org assessment scale.",
    items: [
      "Destination Management & Governance",
      "Visitor Experience",
      "Tourism Infrastructure & Quality",
      "Culture, Heritage & Identity",
      "Community & Economic Impact",
      "Safety, Health & Security",
      "Sustainability & Responsible Tourism",
    ],
  },
  {
    icon: SearchCheck,
    code: "Stage 4",
    title: "Evidence Verification",
    summary:
      "WTTQ.Org reviewers independently verify submitted information. Additional evidence may be requested where necessary.",
    items: [
      "Documentation review",
      "Website evaluation",
      "Tourism data analysis",
      "Stakeholder interviews",
      "Visitor feedback analysis",
      "Public information review",
    ],
    priceCta: {
      label: "Certification Fee — Price Upon Request",
      href: "/#apply",
    },
  },
  {
    icon: Gavel,
    code: "Stage 5",
    title: "Destination Audit",
    summary:
      "Depending on destination size and certification level, WTTQ.Org may conduct one or more audit formats.",
    groups: [
      {
        heading: "Remote Audit",
        items: ["Review of submitted evidence and supporting documentation."],
      },
      {
        heading: "Virtual Audit",
        items: [
          "Online meetings, stakeholder interviews, and virtual inspections.",
        ],
      },
      {
        heading: "On-Site Assessment",
        items: [
          "Field visits and destination inspections conducted by WTTQ.Org assessors.",
          "Areas reviewed: visitor facilities, attractions, public spaces, tourism infrastructure, safety systems, heritage sites, sustainability initiatives.",
        ],
      },
    ],
  },
];

const scoringScale = [
  { score: "0", rating: "Not Available / Non-Compliant" },
  { score: "1", rating: "Poor" },
  { score: "2", rating: "Basic" },
  { score: "3", rating: "Good" },
  { score: "4", rating: "Very Good" },
  { score: "5", rating: "Excellent / Best Practice" },
];

const tiers = [
  {
    name: "Bronze",
    label: "Certified Quality Destination",
    range: "70 – 79",
    badge: BADGE_URLS.bronze,
    accent: "#B87333",
    description:
      "Recognizes destinations that meet established tourism quality standards and provide a reliable and enjoyable visitor experience.",
  },
  {
    name: "Silver",
    label: "Certified Destination Excellence",
    range: "80 – 89",
    badge: BADGE_URLS.silver,
    accent: "#9CA3AF",
    description:
      "Recognizes destinations that consistently exceed visitor expectations and demonstrate strong destination management, community engagement, and tourism performance.",
  },
  {
    name: "Gold",
    label: "Certified World-Class Destination Excellence",
    range: "90 – 100",
    badge: BADGE_URLS.gold,
    accent: "#D4AF37",
    description:
      "Recognizes destinations that demonstrate exceptional performance, international competitiveness, sustainable tourism leadership, and outstanding visitor experiences. Additional Gold requirements may apply.",
  },
];

const goldRequirements = [
  "Destination Excellence Report",
  "Tourism Performance Data",
  "Sustainability Performance Indicators",
  "Community Impact Evidence",
  "Visitor Satisfaction Results",
  "International Recognition",
  "Innovation Programs",
  "Stakeholder Testimonials",
];

const validityPoints = [
  "Annual performance reviews",
  "Monitoring of destination indicators",
  "Compliance verification",
  "Renewal assessments",
];

const whyPoints = [
  "Serves visitors",
  "Supports local communities",
  "Protects culture and heritage",
  "Ensures safety and wellbeing",
  "Promotes sustainability",
  "Delivers memorable experiences",
  "Creates long-term tourism value",
];

const DestinationMethodologyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const pdfUrl = `${BACKEND_URL}/api/methodology/destination/pdf`;

  return (
    <div className="min-h-screen bg-white" data-testid="destination-methodology-page">
      <Header />

      {/* Hero */}
      <section
        className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-[#0B132B] text-white overflow-hidden"
        data-testid="destination-methodology-hero"
      >
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute -top-24 -left-24 w-96 h-96 bg-[#D4AF37] rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-[28rem] h-[28rem] bg-[#D4AF37] rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-6">
              Destination Certification Methodology
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              The global standard for
              <br />
              <span className="text-[#D4AF37]">destination excellence</span>.
            </h1>
            <p className="text-white/80 text-lg leading-relaxed mb-6">
              WTTQ.Org Destination Certification recognizes destinations that
              demonstrate excellence in visitor experience, destination management,
              cultural preservation, community engagement, safety, sustainability,
              and tourism development.
            </p>
            <p className="text-white/70 text-base leading-relaxed">
              An independent, evidence-based benchmark for governments, tourism
              boards, travelers, investors, and industry stakeholders worldwide.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0B132B] px-7 py-3.5 text-sm font-semibold tracking-wider uppercase hover:bg-[#E8C456] transition-colors duration-300"
                data-testid="destination-methodology-download-pdf-hero"
              >
                <Download className="w-4 h-4" />
                Download PDF
              </a>
              <a
                href="#destination-pillars"
                className="inline-flex items-center gap-2 text-white/80 hover:text-[#D4AF37] text-sm font-medium tracking-wide transition-colors border-b border-white/20 hover:border-[#D4AF37] pb-1"
                data-testid="destination-explore"
              >
                Explore the framework
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What is a Destination */}
      <section
        className="py-16 lg:py-20 bg-white border-b border-[#0B132B]/5"
        data-testid="destination-what-is"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
            <div className="lg:col-span-1">
              <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4">
                Eligibility
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0B132B] leading-tight mb-4">
                What is a destination?
              </h2>
              <p className="text-[#0B132B]/70 leading-relaxed">
                For the purpose of WTTQ.Org Certification, a destination may
                include the following categories — from compact municipalities
                to national tourism areas.
              </p>
            </div>
            <div className="lg:col-span-2">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {destinationTypes.map((t) => (
                  <div
                    key={t}
                    className="flex items-center gap-2 bg-[#FAFAFA] border border-[#0B132B]/8 px-4 py-3 text-sm text-[#0B132B]"
                    data-testid={`destination-type-${t.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <MapPinned className="w-4 h-4 text-[#D4AF37] shrink-0" />
                    <span>{t}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Seven Pillars */}
      <section
        id="destination-pillars"
        className="py-20 lg:py-28 bg-[#FAFAFA]"
        data-testid="destination-pillars"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4">
              The Excellence Framework
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B132B] leading-tight">
              Seven core pillars of destination excellence.
            </h2>
            <p className="mt-6 text-base text-[#0B132B]/70 leading-relaxed">
              Destinations are assessed against these foundational pillars —
              each defining a critical dimension of destination quality,
              competitiveness, and responsible tourism development.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {pillars.map((pillar, idx) => {
              const Icon = pillar.icon;
              return (
                <div
                  key={pillar.title}
                  className="group bg-white border border-[#0B132B]/8 p-8 transition-all duration-300 hover:border-[#D4AF37] hover:shadow-[0_20px_60px_-25px_rgba(212,175,55,0.45)]"
                  data-testid={`destination-pillar-card-${idx + 1}`}
                >
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#0B132B] text-[#D4AF37] group-hover:bg-[#D4AF37] group-hover:text-[#0B132B] transition-colors">
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className="text-xs tracking-[0.25em] uppercase text-[#0B132B]/40 font-medium">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="font-heading text-xl font-bold text-[#0B132B] mb-3">
                    {pillar.title}
                  </h3>
                  <p className="text-sm text-[#0B132B]/70 leading-relaxed mb-5">
                    {pillar.description}
                  </p>
                  <div className="pt-4 border-t border-[#0B132B]/8">
                    <p className="text-xs uppercase tracking-widest text-[#0B132B]/45 font-semibold mb-2">
                      Areas Evaluated
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {pillar.areas.map((a) => (
                        <span
                          key={a}
                          className="text-[11px] text-[#0B132B]/70 bg-[#FAFAFA] px-2 py-1 border border-[#0B132B]/8"
                        >
                          {a}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stages */}
      <section
        className="py-20 lg:py-28 bg-white"
        data-testid="destination-stages"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4">
              Assessment Methodology
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B132B] leading-tight">
              Five stages of independent evaluation.
            </h2>
            <p className="mt-6 text-base text-[#0B132B]/70 leading-relaxed">
              Each stage builds a complete portrait of destination quality —
              from application intake through field assessment.
            </p>
          </div>

          <Accordion
            type="single"
            collapsible
            defaultValue="Stage 1"
            className="space-y-3"
            data-testid="destination-stages-accordion"
          >
            {stages.map((stage) => {
              const Icon = stage.icon;
              return (
                <AccordionItem
                  key={stage.code}
                  value={stage.code}
                  className="border border-[#0B132B]/10 bg-[#FAFAFA] px-6 data-[state=open]:bg-white data-[state=open]:border-[#D4AF37] data-[state=open]:shadow-[0_20px_60px_-30px_rgba(11,19,43,0.35)]"
                  data-testid={`destination-stage-${stage.code.replace(" ", "-").toLowerCase()}`}
                >
                  <AccordionTrigger className="hover:no-underline py-6">
                    <div className="flex items-center gap-5 text-left flex-1">
                      <div className="w-12 h-12 flex items-center justify-center bg-[#0B132B] text-[#D4AF37] shrink-0">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-1">
                          {stage.code}
                        </p>
                        <h3 className="font-heading text-xl sm:text-2xl font-bold text-[#0B132B]">
                          {stage.title}
                        </h3>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 pt-2">
                    <p className="text-[#0B132B]/75 leading-relaxed mb-6 max-w-3xl">
                      {stage.summary}
                    </p>

                    {stage.items && (
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                        {stage.items.map((item) => (
                          <li
                            key={item}
                            className="flex items-start gap-2 text-sm text-[#0B132B]/80"
                          >
                            <CheckCircle2 className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}

                    {stage.groups && (
                      <div className="space-y-6">
                        {stage.groups.map((group) => (
                          <div key={group.heading}>
                            <h4 className="font-heading text-base font-bold text-[#0B132B] mb-3 pb-2 border-b border-[#0B132B]/10">
                              {group.heading}
                            </h4>
                            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                              {group.items.map((item) => (
                                <li
                                  key={item}
                                  className="flex items-start gap-2 text-sm text-[#0B132B]/75"
                                >
                                  <span className="w-1 h-1 rounded-full bg-[#D4AF37] mt-2 shrink-0" />
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}

                    {stage.payCta && (
                      <a
                        href={stage.payCta.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-6 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0B132B] px-6 py-3 text-sm font-semibold tracking-wider uppercase hover:bg-[#E8C456] transition-colors duration-300"
                        data-testid={`pay-cta-${stage.code.replace(" ", "-").toLowerCase()}`}
                      >
                        <CreditCard className="w-4 h-4" />
                        {stage.payCta.label}
                        <ArrowRight className="w-4 h-4" />
                      </a>
                    )}

                    {stage.priceCta && (
                      <Link
                        to={stage.priceCta.href}
                        className="mt-6 inline-flex items-center gap-2 bg-[#0B132B] text-white px-6 py-3 text-sm font-semibold tracking-wider uppercase hover:bg-[#1a2542] transition-colors duration-300"
                        data-testid={`price-cta-${stage.code.replace(" ", "-").toLowerCase()}`}
                      >
                        <Tag className="w-4 h-4 text-[#D4AF37]" />
                        {stage.priceCta.label}
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    )}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      </section>

      {/* Scoring */}
      <section
        className="py-20 lg:py-28 bg-[#0B132B] text-white"
        data-testid="destination-scoring"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4">
                Scoring Scale
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6">
                A standardized scale across all pillars.
              </h2>
              <p className="text-white/75 leading-relaxed">
                Each criterion within the Destination Excellence Assessment is
                rated independently on a 0 to 5 scale, producing a composite
                destination score that determines certification tier.
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 backdrop-blur-sm">
              <Table data-testid="destination-scoring-table">
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-[#D4AF37] uppercase text-xs tracking-[0.25em] w-24">
                      Score
                    </TableHead>
                    <TableHead className="text-[#D4AF37] uppercase text-xs tracking-[0.25em]">
                      Rating
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {scoringScale.map((row) => (
                    <TableRow
                      key={row.score}
                      className="border-white/10 hover:bg-white/5"
                      data-testid={`destination-score-row-${row.score}`}
                    >
                      <TableCell className="font-heading text-2xl font-bold text-[#D4AF37]">
                        {row.score}
                      </TableCell>
                      <TableCell className="text-white/90 text-sm">
                        {row.rating}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </section>

      {/* Certification Levels */}
      <section
        className="py-20 lg:py-28 bg-[#FAFAFA]"
        data-testid="destination-tiers"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4">
              Certification Levels
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B132B] leading-tight">
              Three tiers. One destination benchmark.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {tiers.map((tier) => (
              <div
                key={tier.name}
                className="relative bg-white border border-[#0B132B]/10 p-8 flex flex-col"
                style={{ borderTop: `4px solid ${tier.accent}` }}
                data-testid={`destination-tier-card-${tier.name.toLowerCase()}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <p
                      className="text-xs tracking-[0.25em] uppercase font-semibold mb-2"
                      style={{ color: tier.accent }}
                    >
                      {tier.label}
                    </p>
                    <h3 className="font-heading text-3xl font-bold text-[#0B132B]">
                      {tier.name}
                    </h3>
                  </div>
                  <img
                    src={tier.badge}
                    alt={`${tier.name} destination certification badge`}
                    className="w-16 h-16 object-contain"
                  />
                </div>

                <div className="mb-5 pb-5 border-b border-[#0B132B]/10">
                  <p className="text-xs uppercase tracking-widest text-[#0B132B]/50 mb-1">
                    Score Range
                  </p>
                  <p className="font-heading text-2xl font-bold text-[#0B132B]">
                    {tier.range}
                  </p>
                </div>

                <p className="text-sm text-[#0B132B]/75 leading-relaxed">
                  {tier.description}
                </p>
              </div>
            ))}
          </div>

          {/* Gold additional requirements */}
          <div
            className="mt-10 bg-white border border-[#D4AF37]/40 p-8 lg:p-10"
            data-testid="destination-gold-requirements"
          >
            <div className="flex flex-col lg:flex-row lg:items-start gap-8">
              <div className="lg:w-1/3">
                <p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase mb-3">
                  Gold Destination Certification
                </p>
                <h3 className="font-heading text-2xl sm:text-3xl font-bold text-[#0B132B] mb-3">
                  Additional evidence of leadership.
                </h3>
                <p className="text-sm text-[#0B132B]/70 leading-relaxed">
                  Destinations seeking Gold Certification may be required to
                  provide additional evidence demonstrating leadership and
                  excellence. Gold is reserved for destinations demonstrating
                  world-class performance across all assessment pillars.
                </p>
              </div>
              <div className="lg:w-2/3">
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  {goldRequirements.map((req) => (
                    <li
                      key={req}
                      className="flex items-start gap-3 text-sm text-[#0B132B]/85"
                    >
                      <CheckCircle2 className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />
                      <span>{req}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Validity + Why */}
      <section className="py-20 lg:py-28 bg-white" data-testid="destination-validity">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4">
                Certification Validity
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0B132B] leading-tight mb-6">
                Maintained, monitored, and renewed.
              </h2>
              <p className="text-[#0B132B]/75 leading-relaxed mb-6">
                WTTQ.Org Destination Certification is valid for a defined
                certification period and may be subject to:
              </p>
              <ul className="space-y-3">
                {validityPoints.map((point) => (
                  <li
                    key={point}
                    className="flex items-start gap-3 text-[#0B132B]/85"
                  >
                    <CheckCircle2 className="w-5 h-5 text-[#D4AF37] mt-0.5 shrink-0" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-sm text-[#0B132B]/60 italic">
                Certified destinations are expected to maintain standards
                throughout the certification period.
              </p>
            </div>

            <div className="bg-[#0B132B] text-white p-10 lg:p-12 relative overflow-hidden">
              <div className="absolute -top-12 -right-12 w-48 h-48 bg-[#D4AF37]/10 rounded-full blur-3xl pointer-events-none" />
              <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4 relative">
                Why WTTQ.Org Destination Certification?
              </p>
              <h3 className="font-heading text-3xl font-bold leading-tight mb-5 relative">
                Beyond visitor numbers and marketing.
              </h3>
              <p className="text-white/80 leading-relaxed mb-5 relative">
                Destination certification evaluates how effectively a
                destination:
              </p>
              <ul className="space-y-2 relative">
                {whyPoints.map((p) => (
                  <li
                    key={p}
                    className="flex items-start gap-3 text-sm text-white/85"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-2 shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 pt-6 border-t border-white/10 relative">
                <p className="font-heading text-xl font-bold text-white">
                  WTTQ.Org
                </p>
                <p className="text-[#D4AF37] text-sm tracking-wide">
                  The Global Standard for Destination Excellence.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-20 bg-[#FAFAFA] border-t border-[#0B132B]/5"
        data-testid="destination-methodology-cta"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0B132B] mb-5">
            Ready to certify your destination?
          </h2>
          <p className="text-[#0B132B]/70 mb-8 max-w-2xl mx-auto leading-relaxed">
            Apply now to begin the WTTQ.Org Destination Certification process.
            Our independent assessors will guide you through every stage.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/#apply"
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0B132B] px-8 py-4 text-sm font-semibold tracking-wider uppercase hover:bg-[#E8C456] transition-colors duration-300"
              data-testid="destination-methodology-apply-cta"
            >
              Apply for Certification
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-[#0B132B] text-[#0B132B] px-8 py-4 text-sm font-semibold tracking-wider uppercase hover:bg-[#0B132B] hover:text-white transition-colors duration-300"
              data-testid="destination-methodology-download-pdf-cta"
            >
              <Download className="w-4 h-4" />
              Download Methodology PDF
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DestinationMethodologyPage;
